import { supabase } from '../lib/supabase';

/**
 * Conference Service
 * Handles all conference operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

const normalizeAllowedFileTypes = (value) => (
  ['pdf', 'docx', 'both']?.includes(value) ? value : 'both'
);

const normalizeEmail = (email) => email?.toLowerCase()?.trim();

const mapConferenceMember = (entry) => {
  const profile = entry?.user_profiles;
  if (!profile) return null;

  return {
    id: profile?.id,
    name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
    firstName: profile?.first_name,
    lastName: profile?.last_name,
    email: profile?.email,
    institution: profile?.institution,
    department: profile?.department,
    website: profile?.website,
    avatarUrl: profile?.avatar_url,
    academicRank: profile?.academic_rank,
    role: entry?.role
  };
};

export const conferenceService = {
  /**
   * Get all published conferences
   */
  async getAllPublished() {
    try {
      const { data, error } = await supabase
        ?.from('conferences')
        ?.select(`
          id,
          name,
          short_name,
          description,
          website,
          location_type,
          location,
          start_date,
          end_date,
          paper_deadline,
          status,
          subject_area,
          allowed_file_types,
          banner_image_url
        `)
        ?.neq('status', 'draft')
        ?.order('start_date', { ascending: true });

      if (error) throw error;

      return data?.map(conf => ({
        id: conf?.id,
        name: conf?.name,
        shortName: conf?.short_name,
        description: conf?.description,
        website: conf?.website,
        locationType: conf?.location_type,
        location: conf?.location,
        startDate: conf?.start_date,
        endDate: conf?.end_date,
        paperDeadline: conf?.paper_deadline,
        status: conf?.status,
        subjectArea: conf?.subject_area,
        allowedFileTypes: normalizeAllowedFileTypes(conf?.allowed_file_types),
        bannerImageUrl: conf?.banner_image_url,
      })) || [];
    } catch (error) {
      console.error('Get conferences error:', error);
      throw error;
    }
  },

  async getBannerImageUrls(filePaths, expirationSeconds = 604800) {
    try {
      const uniquePaths = [...new Set((filePaths || [])?.filter(Boolean))];
      if (uniquePaths?.length === 0) {
        return new Map();
      }

      const { data, error } = await supabase
        ?.storage
        ?.from('conference-submissions')
        ?.createSignedUrls(uniquePaths, expirationSeconds);

      if (error) throw error;

      return new Map(
        uniquePaths?.map((path, index) => [path, data?.[index]?.signedUrl || null])
      );
    } catch (error) {
      console.warn('Batch banner image lookup failed:', error);
      return new Map();
    }
  },

  /**
   * Get conferences by user role
   */
  async getByUserRole(userId, role) {
    try {
      const { data, error } = await supabase?.from('conference_roles')?.select(`
          conference_id,
          role,
          conferences (
            id, name, short_name, status, paper_deadline, start_date, end_date
          )
        `)?.eq('user_id', userId)?.eq('role', role);

      if (error) throw error;

      return data?.map(item => ({
        id: item?.conferences?.id,
        name: item?.conferences?.name,
        shortName: item?.conferences?.short_name,
        status: item?.conferences?.status,
        deadline: item?.conferences?.paper_deadline,
        startDate: item?.conferences?.start_date,
        endDate: item?.conferences?.end_date,
        role: item?.role
      })) || [];
    } catch (error) {
      console.error('Get conferences by role error:', error);
      throw error;
    }
  },

  /**
   * Get conferences created by user
   */
  async getCreatedByUser(userId) {
    try {
      const { data, error } = await supabase?.from('conferences')?.select('id, name, short_name, status, paper_deadline')?.eq('created_by', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(conf => ({
        id: conf?.id,
        name: conf?.name,
        shortName: conf?.short_name,
        status: conf?.status,
        paper_deadline: conf?.paper_deadline
      })) || [];
    } catch (error) {
      console.error('Get conferences created by user error:', error);
      throw error;
    }
  },

  /**
   * Get conference statistics for user
   */
  async getUserConferenceStats(userId) {
    try {
      const { data, error } = await supabase?.from('conference_roles')?.select(`
          role,
          conferences (id, status)
        `)?.eq('user_id', userId);

      if (error) throw error;

      const stats = {
        totalConferences: new Set(data?.map(r => r?.conferences?.id))?.size || 0,
        asChair: data?.filter(r => r?.role === 'chair')?.length || 0,
        asReviewer: data?.filter(r => r?.role === 'reviewer')?.length || 0,
        asAuthor: data?.filter(r => r?.role === 'author')?.length || 0,
        active: new Set(data?.filter(r => r?.conferences?.status === 'published' || r?.conferences?.status === 'submission_open')?.map(r => r?.conferences?.id))?.size || 0
      };

      return stats;
    } catch (error) {
      console.error('Get user conference stats error:', error);
      throw error;
    }
  },

  /**
   * Get upcoming deadlines for user
   */
  async getUserDeadlines(userId) {
    try {
      const { data, error } = await supabase?.from('conference_roles')?.select(`
          role,
          conferences (
            id, name, short_name, 
            paper_deadline, review_deadline, decision_date, camera_ready_deadline
          )
        `)?.eq('user_id', userId);

      if (error) throw error;

      const deadlines = [];
      const now = new Date();

      data?.forEach(item => {
        const conf = item?.conferences;
        if (!conf) return;

        // Add paper deadline for authors
        if (item?.role === 'author' && conf?.paper_deadline) {
          const deadline = new Date(conf?.paper_deadline);
          if (deadline > now) {
            deadlines?.push({
              id: `${conf?.id}-paper`,
              conferenceId: conf?.id,
              title: 'Paper Submission',
              conference: conf?.short_name || conf?.name,
              date: conf?.paper_deadline,
              type: 'submission'
            });
          }
        }

        // Add review deadline for reviewers
        if (item?.role === 'reviewer' && conf?.review_deadline) {
          const deadline = new Date(conf?.review_deadline);
          if (deadline > now) {
            deadlines?.push({
              id: `${conf?.id}-review`,
              conferenceId: conf?.id,
              title: 'Review Submission',
              conference: conf?.short_name || conf?.name,
              date: conf?.review_deadline,
              type: 'review'
            });
          }
        }

        // Add camera ready deadline for authors with accepted papers
        if (item?.role === 'author' && conf?.camera_ready_deadline) {
          const deadline = new Date(conf?.camera_ready_deadline);
          if (deadline > now) {
            deadlines?.push({
              id: `${conf?.id}-camera`,
              conferenceId: conf?.id,
              title: 'Camera Ready',
              conference: conf?.short_name || conf?.name,
              date: conf?.camera_ready_deadline,
              type: 'camera_ready'
            });
          }
        }
      });

      // Sort by date
      deadlines?.sort((a, b) => new Date(a?.date) - new Date(b?.date));

      return deadlines;
    } catch (error) {
      console.error('Get user deadlines error:', error);
      throw error;
    }
  },

  /**
   * Get conferences where user has submitted papers
   */
  async getConferencesWithUserSubmissions(userId) {
    try {
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          conference_id,
          conferences (
            id, name, short_name, status, paper_deadline, start_date, end_date
          )
        `)?.eq('user_id', userId);

      if (error) throw error;

      // Remove duplicates (user might have multiple submissions to same conference)
      const uniqueConferences = new Map();
      data?.forEach(item => {
        if (item?.conferences && !uniqueConferences?.has(item?.conferences?.id)) {
          uniqueConferences?.set(item?.conferences?.id, {
            id: item?.conferences?.id,
            name: item?.conferences?.name,
            shortName: item?.conferences?.short_name,
            status: item?.conferences?.status,
            deadline: item?.conferences?.paper_deadline,
            startDate: item?.conferences?.start_date,
            endDate: item?.conferences?.end_date
          });
        }
      });

      return Array.from(uniqueConferences?.values()) || [];
    } catch (error) {
      console.error('Get conferences with user submissions error:', error);
      throw error;
    }
  },

  /**
   * Get single conference by ID
   */
  async getById(conferenceId) {
    try {
      const { data, error } = await supabase?.from('conferences')?.select('*')?.eq('id', conferenceId)?.single();

      if (error) throw error;
      if (!data) return null;

      return {
        id: data?.id,
        name: data?.name,
        shortName: data?.short_name,
        description: data?.description,
        callForPapers: data?.call_for_papers,
        callForPapersUrl: data?.call_for_papers_url,
        website: data?.website,
        locationType: data?.location_type,
        venueName: data?.venue_name,
        location: data?.location,
        startDate: data?.start_date,
        endDate: data?.end_date,
        paperDeadline: data?.paper_deadline,
        reviewDeadline: data?.review_deadline,
        decisionDate: data?.decision_date,
        cameraReadyDeadline: data?.camera_ready_deadline,
        status: data?.status,
        subjectArea: data?.subject_area,
        conferenceType: data?.conference_type,
        visibility: data?.visibility,
        featured: data?.featured,
        publicRegistration: data?.public_registration,
        minReviews: data?.min_reviews,
        enableBidding: data?.enable_bidding,
        doubleBlind: data?.double_blind,
        showOtherReviews: data?.show_other_reviews,
        allowedFileTypes: normalizeAllowedFileTypes(data?.allowed_file_types),
        keywords: data?.keywords || [],
        bannerImageUrl: data?.banner_image_url,
        badgeHeaderText: data?.badge_header_text,
        badgeShowInstitution: data?.badge_show_institution ?? true,
        badgeShowQr: data?.badge_show_qr ?? true,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Get conference error:', error);
      throw error;
    }
  },

  /**
   * Create new conference
   */
  async create(conferenceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('conferences')?.insert([{
        name: conferenceData?.conferenceName,
        short_name: conferenceData?.shortName,
        description: conferenceData?.description,
        call_for_papers: conferenceData?.callForPapers,
        website: conferenceData?.website,
        location_type: conferenceData?.locationType,
        venue_name: conferenceData?.venueName,
        location: conferenceData?.location,
        start_date: conferenceData?.startDate,
        end_date: conferenceData?.endDate,
        paper_deadline: conferenceData?.paperDeadline,
        review_deadline: conferenceData?.reviewDeadline,
        decision_date: conferenceData?.decisionDate,
        camera_ready_deadline: conferenceData?.cameraReadyDeadline,
        status: conferenceData?.status || 'draft',
        subject_area: conferenceData?.subjectArea,
        conference_type: conferenceData?.conferenceType,
        visibility: conferenceData?.visibility,
        featured: conferenceData?.featured,
        public_registration: conferenceData?.publicRegistration,
        min_reviews: conferenceData?.minReviews,
        enable_bidding: conferenceData?.enableBidding,
        double_blind: conferenceData?.doubleBlind,
        show_other_reviews: conferenceData?.showOtherReviews,
        allowed_file_types: normalizeAllowedFileTypes(conferenceData?.allowedFileTypes),
        keywords: conferenceData?.keywords || [],
        banner_image_url: conferenceData?.bannerImageUrl || null,
        created_by: user?.id
      }])?.select()?.single();

      if (error) throw error;

      // Add the creator as chair in conference_roles so RLS policies work
      const { error: roleError } = await supabase?.from('conference_roles')?.insert({
        conference_id: data?.id,
        user_id: user?.id,
        role: 'chair'
      });
      if (roleError) {
        console.error('Error adding chair role:', roleError);
        // Don't throw - conference was created successfully
      }

      return {
        id: data?.id,
        name: data?.name,
        shortName: data?.short_name,
        description: data?.description,
        callForPapers: data?.call_for_papers,
        website: data?.website,
        locationType: data?.location_type,
        venueName: data?.venue_name,
        location: data?.location,
        startDate: data?.start_date,
        endDate: data?.end_date,
        paperDeadline: data?.paper_deadline,
        reviewDeadline: data?.review_deadline,
        decisionDate: data?.decision_date,
        cameraReadyDeadline: data?.camera_ready_deadline,
        status: data?.status,
        subjectArea: data?.subject_area,
        conferenceType: data?.conference_type,
        visibility: data?.visibility,
        featured: data?.featured,
        publicRegistration: data?.public_registration,
        minReviews: data?.min_reviews,
        enableBidding: data?.enable_bidding,
        doubleBlind: data?.double_blind,
        showOtherReviews: data?.show_other_reviews,
        allowedFileTypes: normalizeAllowedFileTypes(data?.allowed_file_types),
        keywords: data?.keywords || [],
        bannerImageUrl: data?.banner_image_url,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Conference creation error:', error);
      throw error;
    }
  },

  /**
   * Update conference
   */
  async update(conferenceId, updates) {
    try {
      // Map camelCase to snake_case, supporting both wizard and dashboard field names
      const fieldMap = {
        name: 'name',
        conferenceName: 'name',
        shortName: 'short_name',
        description: 'description',
        callForPapers: 'call_for_papers',
        callForPapersUrl: 'call_for_papers_url',
        website: 'website',
        locationType: 'location_type',
        venueName: 'venue_name',
        location: 'location',
        startDate: 'start_date',
        endDate: 'end_date',
        paperDeadline: 'paper_deadline',
        reviewDeadline: 'review_deadline',
        decisionDate: 'decision_date',
        cameraReadyDeadline: 'camera_ready_deadline',
        status: 'status',
        subjectArea: 'subject_area',
        conferenceType: 'conference_type',
        visibility: 'visibility',
        featured: 'featured',
        publicRegistration: 'public_registration',
        minReviews: 'min_reviews',
        enableBidding: 'enable_bidding',
        doubleBlind: 'double_blind',
        showOtherReviews: 'show_other_reviews',
        allowedFileTypes: 'allowed_file_types',
        keywords: 'keywords',
        bannerImageUrl: 'banner_image_url',
        badgeHeaderText: 'badge_header_text',
        badgeShowInstitution: 'badge_show_institution',
        badgeShowQr: 'badge_show_qr'
      };

      // Only include fields that are actually provided in updates
      const dbUpdates = {};
      for (const [key, value] of Object.entries(updates)) {
        if (fieldMap[key] !== undefined && value !== undefined) {
          dbUpdates[fieldMap[key]] = value;
        }
      }

      const { data, error } = await supabase?.from('conferences')
        ?.update(dbUpdates)
        ?.eq('id', conferenceId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update conference error:', error);
      throw error;
    }
  },

  /**
   * Get conference tracks
   */
  async getTracks(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_tracks')
        ?.select('*')
        ?.eq('conference_id', conferenceId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(track => ({
        id: track?.id,
        conferenceId: track?.conference_id,
        name: track?.name,
        description: track?.description,
        createdAt: track?.created_at
      })) || [];
    } catch (error) {
      console.error('Get tracks error:', error);
      throw error;
    }
  },

  /**
   * Add conference track
   */
  async addTrack(conferenceId, trackData) {
    try {
      const { data, error } = await supabase?.from('conference_tracks')?.insert({
          conference_id: conferenceId,
          name: trackData?.name,
          description: trackData?.description
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Add track error:', error);
      throw error;
    }
  },

  /**
   * Get conference submissions
   */
  async getSubmissions(conferenceId) {
    try {
      console.log('🔍 Fetching submissions for conference:', conferenceId);
      
      // First try with joins including submission_reviews
      let { data, error } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          user_profiles!conference_submissions_user_id_fkey (first_name, last_name, email, institution),
          conference_tracks!conference_submissions_track_id_fkey (name),
          submission_reviews (
            id,
            reviewer_id,
            is_draft,
            submitted_at,
            overall_score,
            confidence_level,
            decision,
            detailed_feedback,
            strengths,
            weaknesses,
            user_profiles!submission_reviews_reviewer_id_fkey (first_name, last_name, email)
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.order('submitted_at', { ascending: false });

      console.log('📊 Query result:', { dataCount: data?.length, error });

      // If join fails, try without joins
      if (error) {
        console.warn('⚠️ Query with joins failed, trying without joins:', error);
        const result = await supabase
          ?.from('conference_submissions')
          ?.select('*')
          ?.eq('conference_id', conferenceId)
          ?.order('submitted_at', { ascending: false });
        
        data = result?.data;
        error = result?.error;
        console.log('📊 Fallback query result:', { dataCount: data?.length, error });
      }

      if (error) {
        console.error('❌ Supabase query error:', error);
        return [];
      }

      if (!data) {
        console.warn('⚠️ No data returned from query');
        return [];
      }

      console.log('✅ Successfully fetched', data?.length, 'submissions');
      console.log('📝 Sample submission data:', data?.[0]);

      const mappedData = data?.map(sub => ({
        id: sub?.id,
        conferenceId: sub?.conference_id,
        userId: sub?.user_id,
        trackId: sub?.track_id,
        trackName: sub?.conference_tracks?.name || 'No Track',
        title: sub?.title,
        abstract: sub?.abstract,
        keywords: sub?.keywords,
        paperNumber: sub?.paper_number,
        pdfUrl: sub?.pdf_url,
        pdfFileName: sub?.pdf_file_name,
        pdfFileSize: sub?.pdf_file_size,
        supplementaryFiles: sub?.supplementary_files,
        coAuthors: sub?.co_authors,
        status: sub?.status,
        submittedAt: sub?.submitted_at,
        updatedAt: sub?.updated_at,
        createdAt: sub?.created_at,
        author: {
          firstName: sub?.user_profiles?.first_name || 'Unknown',
          lastName: sub?.user_profiles?.last_name || 'Author',
          email: sub?.user_profiles?.email || '',
          institution: sub?.user_profiles?.institution || ''
        },
        cameraReadyUrl: sub?.camera_ready_url,
        cameraReadyFileName: sub?.camera_ready_file_name,
        cameraReadyFileSize: sub?.camera_ready_file_size,
        cameraReadySubmittedAt: sub?.camera_ready_submitted_at,
        permissionFormUrl: sub?.permission_form_url || null,
        permissionFormFileName: sub?.permission_form_file_name || null,
        permissionFormSubmittedAt: sub?.permission_form_submitted_at || null,
        revisionSeverity: sub?.revision_severity || null,
        revisionNote: sub?.revision_note || null,
        reviewers: sub?.submission_reviews?.map(review => ({
          id: review?.reviewer_id,
          reviewId: review?.id,
          firstName: review?.user_profiles?.first_name || 'Unknown',
          lastName: review?.user_profiles?.last_name || 'Reviewer',
          email: review?.user_profiles?.email || '',
          reviewStatus: review?.submitted_at ? 'completed' : review?.is_draft ? 'in_progress' : 'pending',
          overallScore: review?.overall_score,
          confidenceLevel: review?.confidence_level,
          decision: review?.decision,
          detailedFeedback: review?.detailed_feedback,
          strengths: review?.strengths,
          weaknesses: review?.weaknesses
        })) || []
      })) || [];
      
      console.log('📦 Returning mapped submissions:', mappedData?.length);
      return mappedData;
    } catch (error) {
      console.error('💥 Get submissions error:', error);
      // Return empty array instead of throwing to prevent page from hanging
      return [];
    }
  },

  /**
   * Upload call for papers PDF
   */
  async uploadCallForPapersPDF(file, conferenceId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `cfp-${conferenceId}-${Date.now()}.${fileExt}`;
      const filePath = `call-for-papers/${fileName}`;

      const { data, error } = await supabase?.storage?.from('conference-submissions')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

      if (error) throw error;

      return data?.path;
    } catch (error) {
      console.error('Upload CFP PDF error:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for call for papers PDF
   */
  async getCallForPapersPDFUrl(filePath) {
    try {
      if (!filePath) return null;

      const { data, error } = await supabase?.storage?.from('conference-submissions')?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error('Get CFP PDF URL error:', error);
      throw error;
    }
  },

  /**
   * Delete call for papers PDF from storage
   */
  async deleteCallForPapersPDF(filePath) {
    try {
      if (!filePath) return;

      const { error } = await supabase?.storage?.from('conference-submissions')?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Delete CFP PDF error:', error);
      throw error;
    }
  },

  /**
   * Upload conference banner image
   */
  async uploadBannerImage(file, conferenceId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `banner-${conferenceId}-${Date.now()}.${fileExt}`;
      const filePath = `banner-images/${fileName}`;

      const { data, error } = await supabase?.storage?.from('conference-submissions')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

      if (error) throw error;

      return data?.path;
    } catch (error) {
      console.error('Upload banner image error:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for conference banner image
   */
  async getBannerImageUrl(filePath) {
    try {
      if (!filePath) return null;

      const { data, error } = await supabase?.storage?.from('conference-submissions')?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.warn('Banner image not found:', filePath);
      return null;
    }
  },

  /**
   * Delete conference banner image from storage
   */
  async deleteBannerImage(filePath) {
    try {
      if (!filePath) return;

      const { error } = await supabase?.storage?.from('conference-submissions')?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Delete banner image error:', error);
      throw error;
    }
  },

  /**
   * Get conference organizers (chairs + organizers) with profile data
   */
  async getConferenceOrganizers(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_roles')
        ?.select(`
          role,
          user_id,
          user_profiles (
            id,
            first_name,
            last_name,
            email,
            institution,
            department,
            website,
            avatar_url,
            academic_rank
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.in('role', ['chair', 'organizer']);

      if (error) throw error;

      const chairs = [];
      const organizers = [];

      data?.forEach(entry => {
        const member = mapConferenceMember(entry);
        if (!member) return;

        if (entry?.role === 'chair') {
          chairs.push(member);
        } else {
          organizers.push(member);
        }
      });

      return { chairs, organizers };
    } catch (error) {
      console.error('Get conference organizers error:', error);
      return { chairs: [], organizers: [] };
    }
  },

  /**
   * Get the full conference team (chairs + organizers + reviewers) with profile data
   */
  async getConferenceTeamMembers(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_roles')
        ?.select(`
          role,
          user_id,
          user_profiles (
            id,
            first_name,
            last_name,
            email,
            institution,
            department,
            website,
            avatar_url,
            academic_rank
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.in('role', ['chair', 'organizer', 'reviewer']);

      if (error) throw error;

      const team = { chairs: [], organizers: [], reviewers: [] };

      data?.forEach(entry => {
        const member = mapConferenceMember(entry);
        if (!member) return;

        if (entry?.role === 'chair') {
          team.chairs.push(member);
        } else if (entry?.role === 'organizer') {
          team.organizers.push(member);
        } else if (entry?.role === 'reviewer') {
          team.reviewers.push(member);
        }
      });

      return team;
    } catch (error) {
      console.error('Get conference team members error:', error);
      return { chairs: [], organizers: [], reviewers: [] };
    }
  },

  /**
   * Add a role to a user for a conference
   */
  async addConferenceRole(conferenceId, userId, role) {
    try {
      const { data, error } = await supabase
        ?.from('conference_roles')
        ?.insert({ conference_id: conferenceId, user_id: userId, role })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Add conference role error:', error);
      throw error;
    }
  },

  /**
   * Transfer conference ownership to another user via the guarded RPC.
   * The database enforces that the recipient holds (and consumes) a publishing
   * entitlement — owning a live conference always costs one, so ownership can't
   * be used to hand out free conferences. The RPC also makes the recipient a
   * chair and removes the previous owner from the team.
   */
  async transferOwnership(conferenceId, newOwnerId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const oldOwnerId = user?.id;
      if (!oldOwnerId) throw new Error('Not authenticated');
      if (newOwnerId === oldOwnerId) throw new Error('You already own this conference.');

      const { error } = await supabase?.rpc('transfer_conference_ownership', {
        p_conference_id: conferenceId,
        p_new_owner: newOwnerId
      });
      if (error) throw error;
    } catch (error) {
      console.error('Transfer ownership error:', error);
      throw error;
    }
  },

  /**
   * Remove a role from a user for a conference
   */
  async removeConferenceRole(conferenceId, userId, role) {
    try {
      const { error } = await supabase
        ?.from('conference_roles')
        ?.delete()
        ?.eq('conference_id', conferenceId)
        ?.eq('user_id', userId)
        ?.eq('role', role);

      if (error) throw error;
    } catch (error) {
      console.error('Remove conference role error:', error);
      throw error;
    }
  },

  /**
   * Invite a team member by email. If they are registered, add the role directly.
   * Either way, send them an email notification.
   * Returns { added: boolean, invited: boolean, profile: object|null }
   */
  async inviteTeamMember(conferenceId, email, role, conferenceName) {
    try {
      const normalizedEmail = normalizeEmail(email);

      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, first_name, last_name, email')
        ?.eq('email', normalizedEmail)
        ?.limit(1);

      const existingProfile = profiles?.[0];
      const { data: { user } } = await supabase?.auth?.getUser();

      if (role === 'reviewer') {
        const { error } = await supabase
          ?.from('conference_invitations')
          ?.upsert({
            conference_id: conferenceId,
            email: normalizedEmail,
            role,
            invited_by: user?.id,
            status: 'pending',
            created_at: new Date()?.toISOString()
          }, {
            onConflict: 'conference_id,email,role'
          });

        if (error) throw error;

        return {
          added: false,
          invited: true,
          profile: existingProfile || null,
          requiresAcceptance: true
        };
      }

      if (existingProfile) {
        await this.addConferenceRole(conferenceId, existingProfile?.id, role);
        return { added: true, invited: false, profile: existingProfile };
      }

      const { error } = await supabase
        ?.from('conference_invitations')
        ?.upsert({
          conference_id: conferenceId,
          email: normalizedEmail,
          role,
          invited_by: user?.id,
          status: 'pending',
          created_at: new Date()?.toISOString()
        }, {
          onConflict: 'conference_id,email,role'
        });

      if (error) throw error;
      return { added: false, invited: true, profile: null };
    } catch (error) {
      console.error('Invite team member error:', error);
      throw error;
    }
  },

  /**
   * Get pending invitations for a conference
   */
  async getPendingInvitations(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_invitations')
        ?.select('*')
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const invitationEmails = [...new Set((data || [])?.map(invitation => normalizeEmail(invitation?.email))?.filter(Boolean))];

      if (invitationEmails?.length === 0) {
        return data || [];
      }

      const { data: matchingProfiles, error: profilesError } = await supabase
        ?.from('user_profiles')
        ?.select('email')
        ?.in('email', invitationEmails);

      if (profilesError) throw profilesError;

      const registeredEmails = new Set(
        (matchingProfiles || [])?.map(profile => normalizeEmail(profile?.email))
      );

      return (data || [])?.map(invitation => ({
        ...invitation,
        hasAccount: registeredEmails?.has(normalizeEmail(invitation?.email))
      }));
    } catch (error) {
      console.error('Get pending invitations error:', error);
      return [];
    }
  },

  /**
   * Get pending reviewer invitations for the current user
   */
  async getMyPendingReviewerInvitations() {
    try {
      const { data, error } = await supabase
        ?.rpc('get_my_pending_reviewer_invitations');

      if (error) throw error;

      return (data || [])?.map(invitation => ({
        id: invitation?.id,
        conferenceId: invitation?.conference_id,
        conferenceName: invitation?.conference_name,
        conferenceShortName: invitation?.conference_short_name,
        invitedBy: invitation?.invited_by,
        invitedByName: invitation?.invited_by_name,
        createdAt: invitation?.created_at
      }));
    } catch (error) {
      console.error('Get pending reviewer invitations error:', error);
      return [];
    }
  },

  /**
   * Accept a pending reviewer invitation for the current user
   */
  async acceptReviewerInvitation(invitationId) {
    try {
      const { data, error } = await supabase
        ?.rpc('accept_reviewer_invitation', {
          p_invitation_id: invitationId
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Accept reviewer invitation error:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(invitationId) {
    try {
      const { error } = await supabase
        ?.from('conference_invitations')
        ?.delete()
        ?.eq('id', invitationId);

      if (error) throw error;
    } catch (error) {
      console.error('Cancel invitation error:', error);
      throw error;
    }
  },
  /**
   * Every conference this user holds any role in, with the roles grouped.
   *
   * The dashboard used to assemble this from six separate calls and a Map. The
   * navigation needs the same picture on every conference-scoped screen, so it
   * gets one query instead. Roles are per conference — a person can chair one,
   * review for another and author in a third, and this returns exactly that.
   */
  async getMyConferences(userId) {
    try {
      const { data, error } = await supabase?.from('conference_roles')?.select(`
          role,
          conference_id,
          conferences ( id, name, short_name, status )
        `)?.eq('user_id', userId);

      if (error) throw error;

      const byId = new Map();
      (data || [])?.forEach((row) => {
        const conf = row?.conferences;
        if (!conf?.id) return;
        const existing = byId?.get(conf?.id);
        if (existing) {
          if (!existing?.roles?.includes(row?.role)) existing?.roles?.push(row?.role);
        } else {
          byId?.set(conf?.id, {
            id: conf?.id,
            name: conf?.name,
            shortName: conf?.short_name,
            status: conf?.status,
            roles: [row?.role],
          });
        }
      });

      return Array.from(byId?.values());
    } catch (error) {
      console.error('Get my conferences error:', error);
      return [];
    }
  },

  /**
   * The roles this user holds in one specific conference.
   * Returns [] rather than throwing: navigation must never be the thing that
   * takes a page down.
   */
  async getMyRolesForConference(userId, conferenceId) {
    try {
      if (!userId || !conferenceId) return [];
      const { data, error } = await supabase
        ?.from('conference_roles')
        ?.select('role')
        ?.eq('user_id', userId)
        ?.eq('conference_id', conferenceId);

      if (error) throw error;
      return Array.from(new Set((data || [])?.map((r) => r?.role)?.filter(Boolean)));
    } catch (error) {
      console.error('Get my roles for conference error:', error);
      return [];
    }
  }

};

export default conferenceService;
